import React, { useState } from 'react';
import { Box, Button, Container, Typography, Divider, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';

const ErrorDebug = () => {
  const navigate = useNavigate();
  const [output, setOutput] = useState('');

  const testDirectNavigation = (statusCode) => {
    setOutput(`Direct navigation to error page ${statusCode} via navigate()`);
    navigate(`/error/${statusCode}`);
  };

  const testApiCall = (statusCode) => {
    setOutput(`Testing API error ${statusCode}...`);
    axios.get(`/api/test-error-${statusCode}`)
      .then(() => {
        setOutput('This message should not appear (API should return error)');
      })
      .catch(err => {
        setOutput(`Caught error: ${err.message}. Should automatically navigate to error page.`);
      });
  };

  const testSessionErrorStorage = (statusCode) => {
    setOutput(`Saving error ${statusCode} to sessionStorage and navigating...`);
    const errorData = {
      status: statusCode,
      message: `Test error ${statusCode}`,
      timestamp: new Date().toISOString()
    };
    sessionStorage.setItem('lastApiError', JSON.stringify(errorData));
    navigate(`/error/${statusCode}`);
  };

  const testWindowLocation = (statusCode) => {
    setOutput(`Redirecting via window.location.href to /error/${statusCode}`);
    window.location.href = `/error/${statusCode}`;
  };

  const testThrowError = () => {
    setOutput('Generating exception to test ErrorBoundary');
    throw new Error('Test error for ErrorBoundary');
  };

  const styles = {
    button: {
      margin: '10px',
      minWidth: '220px'
    },
    output: {
      padding: '15px',
      background: '#f5f5f5',
      borderRadius: '4px',
      whiteSpace: 'pre-wrap',
      fontFamily: 'monospace',
      marginTop: '20px',
      minHeight: '100px'
    }
  };

  return (
    <Container maxWidth="md" sx={{ paddingTop: 4, paddingBottom: 4 }}>
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Error Pages Debug
        </Typography>
        <Typography variant="body1" paragraph>
          Use these buttons to test different ways of displaying error pages.
        </Typography>

        <Divider sx={{ marginY: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          1. Direct navigation to error page via React Router
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Button variant="contained" color="primary" sx={styles.button} onClick={() => testDirectNavigation(404)}>
            404 - React Router
          </Button>
          <Button variant="contained" color="error" sx={styles.button} onClick={() => testDirectNavigation(500)}>
            500 - React Router
          </Button>
          <Button variant="contained" color="warning" sx={styles.button} onClick={() => testDirectNavigation('maintenance')}>
            Maintenance - React Router
          </Button>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Typography variant="h6" gutterBottom>
          2. Testing API errors (via axios interceptor)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Button variant="outlined" color="primary" sx={styles.button} onClick={() => testApiCall(404)}>
            404 - API Error
          </Button>
          <Button variant="outlined" color="error" sx={styles.button} onClick={() => testApiCall(500)}>
            500 - API Error
          </Button>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Typography variant="h6" gutterBottom>
          3. Testing via sessionStorage + navigation
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Button variant="outlined" color="secondary" sx={styles.button} onClick={() => testSessionErrorStorage(404)}>
            404 - SessionStorage
          </Button>
          <Button variant="outlined" color="secondary" sx={styles.button} onClick={() => testSessionErrorStorage(500)}>
            500 - SessionStorage
          </Button>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Typography variant="h6" gutterBottom>
          4. Testing via window.location.href (full page reload)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Button variant="contained" color="info" sx={styles.button} onClick={() => testWindowLocation(404)}>
            404 - window.location
          </Button>
          <Button variant="contained" color="info" sx={styles.button} onClick={() => testWindowLocation(500)}>
            500 - window.location
          </Button>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Typography variant="h6" gutterBottom>
          5. Test ErrorBoundary (throw exception)
        </Typography>
        <Box>
          <Button variant="contained" color="error" sx={styles.button} onClick={testThrowError}>
            Generate Exception
          </Button>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Typography variant="h6" gutterBottom>
          Result:
        </Typography>
        <Box sx={styles.output}>
          {output || 'Test results will be displayed here...'}
        </Box>
      </Paper>
    </Container>
  );
};

export default ErrorDebug; 